import React from 'react';
import type { PresentationSettings as Settings } from '../config/presentationSettings';
import { riderNickname } from '../utils/displayName';
import { unhideRider, useHiddenRiders } from '../utils/hiddenRiders';

const OPTIONS: { key: keyof Settings; label: string; hint: string }[] = [
  { key: 'showCurrentConditions', label: 'Current conditions card', hint: 'Show the extra run summary beneath a short live-player list.' },
  { key: 'showSlopeTrace', label: 'Slope trace graph', hint: 'Draw the large multiplier trace across the bottom of the slope.' },
  { key: 'showRunLevelGraph', label: 'Run-level graph', hint: 'Show level, progress and live-return detail below the slope.' },
  { key: 'showRunLevelTitles', label: 'Run-level title cards', hint: 'Show Carve, Exposure and Whiteout text during level changes. The snow streak remains when off.' },
];

interface Props {
  settings: Settings;
  onChange: (key: keyof Settings, value: boolean) => void;
  onClose: () => void;
}

/** Riders hidden from Live bets, Top riders and the slope, and the way back. */
export const HiddenRidersSettings: React.FC = () => {
  const hidden = useHiddenRiders();
  return (
    <section className="hidden-riders" aria-labelledby="hidden-riders-heading">
      <h3 id="hidden-riders-heading">Hidden riders</h3>
      {hidden.length === 0
        ? <p className="settings-note">No hidden riders. Use the ⋯ button next to a rider to hide their name and colour.</p>
        : <ul className="settings-list">
          {hidden.map(id => (
            <li key={id}>
              <span>{riderNickname(id)}</span>
              <button type="button" className="btn-quiet" aria-label={`Unhide ${riderNickname(id)}`} onClick={() => unhideRider(id)}>Unhide</button>
            </li>
          ))}
        </ul>}
    </section>
  );
};

const PresentationSettings: React.FC<Props> = ({ settings, onChange, onClose }) => (
  <div className="presentation-settings panel">
    <div className="panel-heading">
      <div><p className="section-label">Display</p><h2>Presentation settings</h2></div>
      <button className="auth-switch settings-close" onClick={onClose}>Close</button>
    </div>
    <p className="settings-intro">Choose what appears around the run.</p>
    <div className="presentation-option-list">
      {OPTIONS.map(option => (
        <label className="presentation-option" key={option.key}>
          <span><strong>{option.label}</strong><small>{option.hint}</small></span>
          <input type="checkbox" checked={settings[option.key]} onChange={event => onChange(option.key, event.target.checked)} />
        </label>
      ))}
    </div>
    <HiddenRidersSettings />
    <p className="settings-note">Saved on this device. These settings never change rounds, wagers or payouts.</p>
  </div>
);

export default PresentationSettings;
